/**
 * Week view: Monday to Sunday.
 *
 * A read-only summary of the week — day type, location and what was done each
 * day — followed by what is still planned and what is still outstanding. The
 * "Status note" button prints the client-facing weekly update.
 */

import { button, clear, el } from '../lib/dom.js';
import {
  addDays,
  formatDate,
  formatDateShort,
  isValidKey,
  startOfWeek,
  today,
  weekDays,
} from '../lib/dates.js';
import { billableValue, daysOpen, formatDays, locationLabel, openWaitingOn } from '../lib/model.js';
import * as store from '../lib/store.js';
import { printStatusNote } from '../lib/print.js';

export async function renderWeek(container, params, app) {
  const anchor = isValidKey(params.date) ? params.date : today();
  const monday = startOfWeek(anchor);
  const dates = weekDays(monday);
  const settings = await store.getSettings();
  const days = await store.getDaysBetween(dates[0], dates[6]);
  const byDate = new Map(days.map((d) => [d.date, d]));

  clear(container);

  container.append(
    el('div', { class: 'daynav' }, [
      button('‹', {
        class: 'btn btn--icon',
        'aria-label': 'Previous week',
        title: 'Previous week',
        onclick: () => app.go(`#/week/${addDays(monday, -7)}`),
      }),
      button('›', {
        class: 'btn btn--icon',
        'aria-label': 'Next week',
        title: 'Next week',
        onclick: () => app.go(`#/week/${addDays(monday, 7)}`),
      }),
      el('div', { class: 'daynav__date' }, [
        el('h2', { class: 'daynav__title' }, `Week commencing ${formatDate(monday)}`),
        el('p', { class: 'daynav__sub' }, `${formatDate(dates[0])} to ${formatDate(dates[6])}`),
      ]),
      el('input', {
        type: 'date',
        class: 'input',
        value: monday,
        'aria-label': 'Jump to a week',
        onchange: (event) => {
          if (isValidKey(event.target.value)) app.go(`#/week/${event.target.value}`);
        },
      }),
      monday === startOfWeek(today())
        ? null
        : button('This week', { class: 'btn btn--small', onclick: () => app.go('#/week') }),
      button('Status note', {
        class: 'btn btn--primary',
        onclick: () => printStatusNote({ weekStart: monday, days, settings }),
      }),
    ]),
  );

  container.append(renderSummary(days));
  container.append(renderDayList(dates, byDate, app));
  container.append(renderPlannedNext(days));
  container.append(renderWaiting(days, dates[6]));
}

function renderSummary(days) {
  const billable = days.reduce((sum, day) => sum + billableValue(day), 0);
  const expenses = days.reduce(
    (sum, day) => sum + day.expenses.reduce((n, x) => n + (Number(x.amount) || 0), 0),
    0,
  );
  const doneCount = days.reduce((n, day) => n + day.done.length, 0);

  return el('section', { class: 'card' }, [
    el('div', { class: 'stats' }, [
      stat(formatDays(billable), billable === 1 ? 'billable day' : 'billable days'),
      stat(String(doneCount), doneCount === 1 ? 'item done' : 'items done'),
      stat(
        new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(expenses),
        'expenses',
      ),
    ]),
  ]);
}

function stat(value, label) {
  return el('div', { class: 'stat' }, [
    el('span', { class: 'stat__value' }, value),
    el('span', { class: 'stat__label' }, label),
  ]);
}

function renderDayList(dates, byDate, app) {
  const list = el('div', { class: 'weekdays' });

  for (const date of dates) {
    const day = byDate.get(date);
    const isToday = date === today();

    const head = el('div', { class: 'weekday__head' }, [
      el('span', { class: 'weekday__date' }, [
        el(
          'a',
          {
            href: `#/today/${date}`,
            title: 'Open this day',
          },
          formatDateShort(date),
        ),
      ]),
      el(
        'span',
        { class: 'weekday__meta' },
        day ? `${day.dayType} · ${locationLabel(day)}` : isToday ? 'Today · nothing recorded yet' : 'Nothing recorded',
      ),
    ]);

    const children = [head];

    if (day && day.done.length) {
      children.push(
        el(
          'ul',
          { class: 'weekday__list' },
          day.done.map((entry) =>
            el('li', [
              entry.text,
              entry.workstream ? el('span', { class: 'ws-tag' }, ` — ${entry.workstream}`) : null,
            ]),
          ),
        ),
      );
    } else if (day && day.dayType !== 'Not working') {
      children.push(el('p', { class: 'empty' }, 'No work logged.'));
    }

    list.append(
      el(
        'article',
        { class: `weekday${day ? '' : ' weekday--empty'}` },
        children,
      ),
    );
  }

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [el('h2', { class: 'card__title' }, 'Each day')]),
    list,
  ]);
}

/**
 * Planned items still open at the end of the week.
 *
 * The same item is copied from day to day as it rolls over, so identical lines
 * are collapsed and only the most recent state counts.
 */
function renderPlannedNext(days) {
  const seen = new Map();
  for (const day of [...days].sort((a, b) => a.date.localeCompare(b.date))) {
    for (const entry of day.planned) {
      if (!entry.text.trim()) continue;
      seen.set(`${entry.workstream}|${entry.text.trim().toLowerCase()}`, entry);
    }
  }
  const open = [...seen.values()].filter((entry) => !entry.done);

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Planned next'),
      el('p', { class: 'card__hint' }, 'Open items from this week.'),
    ]),
    open.length
      ? el(
          'ul',
          { class: 'weekday__list' },
          open.map((entry) =>
            el('li', [
              entry.text,
              entry.workstream ? el('span', { class: 'ws-tag' }, ` — ${entry.workstream}`) : null,
            ]),
          ),
        )
      : el('p', { class: 'empty' }, 'Nothing outstanding.'),
  ]);
}

function renderWaiting(days, asAt) {
  const open = openWaitingOn(days);

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Waiting on'),
      el('p', { class: 'card__hint' }, 'Still open at the end of the week.'),
    ]),
    open.length
      ? el(
          'ul',
          { class: 'weekday__list' },
          open.map((item) => {
            const age = daysOpen(item, asAt);
            return el('li', [
              el('strong', item.who || 'Unassigned'),
              ` — ${item.what}`,
              el(
                'span',
                { class: 'ws-tag' },
                ` (${age} ${age === 1 ? 'day' : 'days'}, since ${formatDate(item.sinceDate)})`,
              ),
            ]);
          }),
        )
      : el('p', { class: 'empty' }, 'Nothing outstanding.'),
  ]);
}
